<template>
  <div class="locations-map-card">
    <div class="locations-map-card-close" @click="closeCard">
      <Icon name="close" />
    </div>
    <div class="locations-map-card-bg">
      <AdaptiveMedia
        class="locations-map-card-bg-image"
        :showImageRedius="false"
        is-background
        :field="background"
      />
    </div>
    <div
      class="locations-map-card-swiper"
      ref="swiperEl"
      v-if="item?.fields?.mediaList?.length > 0"
    >
      <div class="swiper-wrapper">
        <div
          class="swiper-slide locations-map-card-item"
          v-for="(media, index) in item.fields.mediaList"
          :key="media.id || index"
        >
          <AdaptiveMedia
            class="locations-map-card-item-media"
            :showImageRedius="false"
            is-background
            :field="media.fields.media"
          />
        </div>
      </div>
      <div
        class="locations-map-card-pagination swiper-pagination"
        ref="paginationEl"
        v-show="mediaList.length > 1"
      />
    </div>
    <div class="locations-map-card-country" v-if="item?.fields?.country?.value">
      <siteImage
        class="locations-map-card-country-image"
        :field="item?.fields?.countryIcon"
        :is-background="false"
      />
      <JssRichText
        class="locations-map-card-country-name"
        :field="item?.fields?.country"
        tag="h2"
      />
    </div>
    <div class="locations-map-card-address" v-if="item?.fields?.address?.value">
      <div class="locations-map-card-address-icon">
        <Icon :svg="positionIcon?.value?.svgCode" />
      </div>
      <JssRichText class="locations-map-card-address-name" :field="item?.fields?.address" />
    </div>
    <div class="locations-map-card-phone" v-if="item?.fields?.mobile?.value">
      <div class="locations-map-card-phone-icon">
        <Icon :svg="phoneIcon?.value?.svgCode" />
      </div>
      <JssRichText class="locations-map-card-phone-name" :field="item?.fields?.mobile" />
    </div>
    <div class="locations-map-card-email" v-if="item?.fields?.email?.value">
      <div class="locations-map-card-email-icon">
        <Icon :svg="messageIcon?.value?.svgCode" />
      </div>
      <JssRichText class="locations-map-card-email-name" :field="item?.fields?.email" />
    </div>
    <div class="locations-map-card-button">
      <siteButton v-bind="item?.fields?.button" />
    </div>
  </div>
</template>

<script>
import { ref, computed, nextTick, onMounted, onBeforeUnmount, watch } from 'vue';
import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';
import { destroySwiper } from '@/utils/site-utils';
import 'swiper/css';

export default {
  name: 'MapCard',
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
    background: {
      type: Object,
      default: () => ({}),
    },
    positionIcon: {
      type: Object,
      default: () => ({}),
    },
    phoneIcon: {
      type: Object,
      default: () => ({}),
    },
    messageIcon: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    let swiper = null;
    const swiperEl = ref(null);
    const paginationEl = ref(null);

    const mediaList = computed(() => props.item?.fields?.mediaList || []);

    const buildSwiper = () => {
      if (!swiperEl.value || !paginationEl.value) return;
      const slidesLen = mediaList.value.length;
      if (!slidesLen) return;

      destroySwiper(swiper, true, true);
      swiper = new Swiper(swiperEl.value, {
        slidesPerView: 1,
        observer: true,
        modules: [Pagination],
        pagination: {
          el: paginationEl.value,
          clickable: true,
          bulletElement: 'span',
        },
      });
    };

    const closeCard = () => {
      emit('close');
    };

    onMounted(() => {
      if (mediaList.value.length > 0) {
        nextTick(() => buildSwiper());
      }
    });

    watch(
      () => mediaList.value.length,
      (newLen, oldLen) => {
        if (newLen > 0) {
          nextTick(() => buildSwiper());
        }
      }
    );

    onBeforeUnmount(() => {
      destroySwiper(swiper, true, true);
    });

    return {
      mediaList,
      swiperEl,
      paginationEl,
      closeCard,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.locations-map-card {
  position: relative;
  width: 100%;
  padding: 24px;
  border: 1px solid $neutral-disable-surface;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(24, 53, 143, 0.12);
  overflow: hidden;
  margin-top: 16px;
  background: $white;
  z-index: 999;

  &-close {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
    cursor: pointer;
    width: 12px;
    height: 12px;
    background: transparent;
    border: none;
    outline: none;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &-bg {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 313px;
    height: 234px;
    z-index: -1;

    &-image {
      width: 100%;
      height: 100%;
    }
  }

  &-swiper {
    position: relative;
    width: 100%;
    height: 160px;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 24px;
  }

  &-item {
    width: 100%;
    height: 100%;

    &-media {
      width: 100%;
      height: 100%;

      > div,
      .background-image,
      img {
        width: 100%;
        height: 100%;
      }

      img {
        object-fit: cover;
        object-position: center;
      }
    }
  }

  &-pagination {
    position: absolute;
    bottom: 12px;
    left: 50%;
    right: auto;
    top: auto;
    transform: translateX(-50%);
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: auto;
    text-align: center;
  }

  .locations-map-card-swiper > .locations-map-card-pagination.swiper-pagination,
  .locations-map-card-swiper
    > .locations-map-card-pagination.swiper-pagination-bullets.swiper-pagination-horizontal {
    bottom: 12px;
    left: 50%;
    right: auto;
    top: auto;
    width: auto;
    transform: translateX(-50%);
  }

  .locations-map-card-pagination.swiper-pagination .swiper-pagination-bullet {
    width: 17px;
    height: 8px;
    background: #c7d3c7;
    margin: 0;
    opacity: 1;
    cursor: pointer;
    border-radius: 10px;
    transform: skewX(-30deg);
    transition: all 0.3s ease;

    &.swiper-pagination-bullet-active {
      background: $blue-night;
    }
  }

  &-country {
    display: flex;
    align-items: center;
    gap: 12px;

    svg {
      width: 24px;
      height: 24px;
    }

    &-name {
      font-size: 18px;
      font-weight: 700;
      line-height: 1.2;
      color: $grey-black;
    }
  }

  &-address {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 24px;

    &-icon {
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &-name {
      font-size: 14px;
      line-height: 1.4;
      color: $grey-black;
    }
  }

  &-phone {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;

    &-icon {
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &-name {
      font-size: 14px;
      line-height: 1.4;
      color: $grey-black;
    }
  }

  &-email {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;

    &-icon {
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &-name {
      font-size: 14px;
      line-height: 1.4;
      color: $grey-black;
    }
  }

  &-button {
    margin-top: 24px;
  }

  @include tablet-landscape {
    margin-top: 0;
    width: 384px;
  }
}
</style>
